// Пункти бічного меню (SPEC розд. 11, BUILD §7.2). Порядок показу — поле `order` (не порядок у масиві), тож кожен модуль
// додає/змінює СВОЇ пункти лише між своїми маркерами // <Uxx> … // </Uxx> — без конфліктів злиття.
// `permission` — клієнтське приховування (src/lib/permissions.ts); права перевіряє сервер. Іконки — lucide-react.
import type { LucideIcon } from 'lucide-react';
import {
  Bell,
  Briefcase,
  Calendar,
  CalendarClock,
  CalendarDays,
  ChartColumn,
  Cpu,
  GitBranch,
  Library,
  Network,
  ScrollText,
  Settings,
  ShieldCheck,
  TriangleAlert,
  UserRound,
} from 'lucide-react';
import type { Permission } from '@/lib/permissions';
import type { AuthUser } from '@/lib/types';

export interface NavChild {
  id: string;
  label: string;
  to: string;
  permission?: Permission;
}

export interface NavItem {
  id: string;
  label: string;
  to: string;
  icon: LucideIcon;
  /** Порядок у меню (менше — вище). */
  order: number;
  /** 'main' — робочі розділи, 'admin' — нижня група (аудит, адміністрування). */
  section: 'main' | 'admin';
  /** Показати, якщо є право (без права — пункт приховано). */
  permission?: Permission;
  /** Підпункти (розкривна група); група без видимих підпунктів ховається. */
  children?: NavChild[];
  /** Активний лише при точному збігу шляху. */
  end?: boolean;
  /** Умова показу за користувачем (напр. лише з пов'язаним викладачем); без неї — лише `permission`. */
  visible?: (user: AuthUser) => boolean;
  /** Лічильник біля пункту: GET `path` → `{ count }` (0 — не показується); `permission` — запитувати лише з цим правом. */
  badge?: NavBadgeSource;
}

export interface NavBadgeSource {
  /** Ключ TanStack Query (інвалідація разом із ключами модуля). */
  queryKey: readonly unknown[];
  /** Шлях API без префікса /api. */
  path: string;
  permission?: Permission;
}

export const NAV_ITEMS: NavItem[] = [
  // <U01>
  { id: 'directories', label: 'Довідники', to: '/directories', icon: Library, order: 70, section: 'main', permission: 'directories.view' },
  // </U01>
  // <U02>
  { id: 'structure', label: 'Структура університету', to: '/structure', icon: Network, order: 72, section: 'main', permission: 'directories.view' },
  // </U02>
  // <U03>
  // </U03>
  // <U04>
  // </U04>
  // <U05>
  {
    id: 'my-availability',
    label: 'Моя доступність',
    to: '/my/availability',
    icon: CalendarClock,
    order: 8,
    section: 'main',
    permission: 'mySchedule.view',
    visible: (u) => !!u.teacherId,
  },
  // </U05>
  // <U06>
  { id: 'calendar-year', label: 'Календар року', to: '/calendar', icon: CalendarDays, order: 75, section: 'main', permission: 'directories.view' },
  // </U06>
  // <U07>
  { id: 'workload', label: 'Навантаження', to: '/workload', icon: Briefcase, order: 60, section: 'main', permission: 'workload.view' },
  // </U07>
  // <U08>
  { id: 'validation', label: 'Перевірка даних', to: '/validation', icon: ShieldCheck, order: 50, section: 'main', permission: 'validation.view' },
  // </U08>
  // <U09>
  // </U09>
  // <U10>
  { id: 'generation', label: 'Генерація', to: '/generation', icon: Cpu, order: 20, section: 'main', permission: 'generation.run' },
  // </U10>
  // <U11>
  { id: 'conflicts', label: 'Конфлікти', to: '/conflicts', icon: TriangleAlert, order: 30, section: 'main', permission: 'conflicts.view' },
  // </U11>
  // <U12>
  { id: 'schedule', label: 'Розклад', to: '/schedule', icon: Calendar, order: 10, section: 'main', permission: 'schedule.viewAll' },
  // </U12>
  // <U13>
  {
    id: 'versions',
    label: 'Версії і погодження',
    to: '/versions',
    icon: GitBranch,
    order: 40,
    section: 'main',
    permission: 'versions.view',
    badge: { queryKey: ['versions', 'inbox', 'count'], path: '/approvals/inbox/count', permission: 'approval.decide' },
  },
  // </U13>
  // <U14>
  { id: 'my', label: 'Мій розклад', to: '/my', icon: UserRound, order: 5, section: 'main', permission: 'mySchedule.view', end: true },
  // </U14>
  // <U15>
  { id: 'analytics', label: 'Аналітика', to: '/analytics', icon: ChartColumn, order: 80, section: 'main', permission: 'analytics.view' },
  // </U15>
  // <U16>
  { id: 'notifications', label: 'Сповіщення', to: '/notifications', icon: Bell, order: 85, section: 'admin' },
  { id: 'audit', label: 'Аудит', to: '/audit', icon: ScrollText, order: 90, section: 'admin', permission: 'audit.view' },
  {
    id: 'admin',
    label: 'Адміністрування',
    to: '/admin',
    icon: Settings,
    order: 100,
    section: 'admin',
    children: [
      { id: 'admin-users', label: 'Користувачі й ролі', to: '/admin/users', permission: 'users.manage' },
      { id: 'admin-rules', label: 'Правила розкладу', to: '/admin/rules', permission: 'rules.edit' },
      { id: 'admin-weights', label: 'Профілі ваг', to: '/admin/weights', permission: 'rules.edit' },
      { id: 'admin-outbox', label: 'Черга листів', to: '/admin/outbox', permission: 'users.manage' },
    ],
  },
  // </U16>
  // <U17>
  // </U17>
];

/** Видимі пункти для прав користувача, відсортовані за order; група без видимих підпунктів ховається. */
export function visibleNav(can: (p: Permission) => boolean, user?: AuthUser | null): NavItem[] {
  return NAV_ITEMS.filter((i) => !i.permission || can(i.permission))
    .filter((i) => !i.visible || (!!user && i.visible(user)))
    .map((i) => (i.children ? { ...i, children: i.children.filter((c) => !c.permission || can(c.permission)) } : i))
    .filter((i) => !i.children || i.children.length > 0)
    .sort((a, b) => a.order - b.order);
}
